import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useDeleteCalendarEventMutation,
  useGetCalendarViewQuery,
  useGetDepartmentOptionsQuery,
  useGetProjectOptionsQuery,
  useGetUserOptionsQuery,
} from '../../../api/serverStateHooks'
import { appConfirm } from '../../../components/AppConfirmModal'
import { appToast } from '../../../components/AppToast'
import SearchableCombobox from '../../../components/SearchableCombobox'
import { parseStoredUser } from '../../../utils/authSession'
import BridgeActionCenterModal from './components/BridgeActionCenterModal'
import DrillDownLink from './components/DrillDownLink'
import CalendarSharingSettings from './components/CalendarSharingSettings'

const VIEW_OPTIONS = ['month', 'week', 'day', 'agenda']
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOUR_LABELS = Array.from({ length: 24 }).map((_, hour) => {
  if (hour === 0) return '12 AM'
  if (hour < 12) return `${hour} AM`
  if (hour === 12) return '12 PM'
  return `${hour - 12} PM`
})
const DAY_ROW_HEIGHT = 56

function formatDateKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function toMonthYear(date) {
  return date.toLocaleDateString([], { month: 'long', year: 'numeric' })
}

function formatToolbarTitle(date, view) {
  if (view === 'day') {
    return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
  }
  if (view === 'week') {
    const start = getWeekStart(date)
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    return `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`
  }
  if (view === 'agenda') {
    const end = new Date(date)
    end.setDate(date.getDate() + 14)
    return `Agenda: ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`
  }
  return date.toLocaleDateString([], { month: 'long', year: 'numeric' })
}

function getWeekStart(date) {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  start.setDate(start.getDate() - start.getDay())
  return start
}

function getMonthMatrix(anchorDate) {
  const firstDay = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1)
  const start = getWeekStart(firstDay)

  const weeks = []
  for (let row = 0; row < 6; row += 1) {
    const days = []
    for (let col = 0; col < 7; col += 1) {
      const current = new Date(start)
      current.setDate(start.getDate() + row * 7 + col)
      days.push(current)
    }
    weeks.push(days)
  }
  return weeks
}

function getMiniMonthMatrix(anchorDate) {
  return getMonthMatrix(anchorDate).flat()
}

function groupByDate(events) {
  const map = new Map()
    ; (events || []).forEach((event) => {
      const start = new Date(event.startTime)
      const key = formatDateKey(start)
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(event)
    })
  return map
}

function formatClock(iso) {
  if (!iso) return '--'
  const date = new Date(iso)
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function toTimeLabel(hour, minute = 0) {
  const period = hour >= 12 ? 'PM' : 'AM'
  const normalized = hour % 12 || 12
  if (minute === 0) return `${normalized} ${period}`
  return `${normalized}:${String(minute).padStart(2, '0')} ${period}`
}

function toHHMM(hour, minute = 0) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function resolveEventPalette(event) {
  if (event.isMasked) {
    return {
      chip: 'bg-slate-200 text-slate-700 border border-slate-300 border-dashed',
      block: 'border-slate-400 bg-slate-100 text-slate-800 border-dashed',
    }
  }
  if (event.scheduleType === 'LEAVE') {
    if (event.approvalStatus === 'PENDING') {
      return {
        chip: 'bg-amber-500 text-white',
        block: 'border-amber-500 bg-amber-50 text-amber-900',
      }
    }
    if (event.approvalStatus === 'REJECTED') {
      return {
        chip: 'bg-rose-400 text-white line-through opacity-80',
        block: 'border-rose-300 bg-rose-50 text-rose-800 line-through',
      }
    }
    return {
      chip: 'bg-rose-600 text-white',
      block: 'border-rose-700 bg-rose-100 text-rose-900',
    }
  }
  if (event.scheduleType === 'HOLIDAY') {
    return {
      chip: 'bg-indigo-600 text-white',
      block: 'border-indigo-700 bg-indigo-100 text-indigo-900',
    }
  }
  if (event.eventType === 'meeting') {
    return {
      chip: 'bg-emerald-700 text-white',
      block: 'border-emerald-700 bg-emerald-100 text-emerald-900',
    }
  }
  if (event.scheduleType === 'AVAILABILITY') {
    return {
      chip: 'bg-blue-600 text-white',
      block: 'border-blue-600 bg-blue-100 text-blue-900',
    }
  }
  return {
    chip: 'bg-sky-600 text-white',
    block: 'border-sky-600 bg-sky-100 text-sky-900',
  }
}

function getEventBlockStyle(event, dayHeight) {
  const start = new Date(event.startTime)
  const end = new Date(event.endTime)
  const startMin = start.getHours() * 60 + start.getMinutes()
  const endMinRaw = end.getHours() * 60 + end.getMinutes()
  const endMin = endMinRaw <= startMin ? startMin + 30 : endMinRaw
  const top = (startMin / 1440) * dayHeight
  const height = Math.max(((endMin - startMin) / 1440) * dayHeight, 24)
  return { top: `${top}px`, height: `${height}px` }
}

function getSlotRangeFromClick(event, dayHeight) {
  const rect = event.currentTarget.getBoundingClientRect()
  const y = Math.max(0, Math.min(event.clientY - rect.top, dayHeight - 1))
  const slotHour = Math.floor(y / DAY_ROW_HEIGHT)
  const startHour = Math.max(0, Math.min(slotHour, 23))
  const endHour = Math.min(startHour + 1, 23)
  const endTime = startHour === 23 ? '23:59' : toHHMM(endHour)
  return {
    startTime: toHHMM(startHour),
    endTime,
    slotLabel: `${toTimeLabel(startHour)} - ${startHour === 23 ? '11:59 PM' : toTimeLabel(endHour)}`,
  }
}

function isEventVisible(event, filters, hasNerve = true) {
  if (event.scheduleType === 'HOLIDAY') {
    return Boolean(filters.holiday)
  }
  if (event.scheduleType === 'LEAVE') {
    if (event.approvalStatus === 'PENDING') {
      return Boolean(filters.pendingLeave)
    }
    return Boolean(filters.approvedLeave)
  }
  if (event.eventType === 'meeting') {
    return Boolean(filters.meetings)
  }
  if (event.eventType === 'work_time' || Boolean(event.jobId) || event.scheduleType === 'JOB') {
    if (!hasNerve) return false
    return Boolean(filters.assignedJob)
  }
  return true
}

function EventPill({ event, onOpen }) {
  const palette = resolveEventPalette(event)
  const isPendingLeave = event.scheduleType === 'LEAVE' && event.approvalStatus === 'PENDING'
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex w-full items-center gap-1 truncate rounded-md px-2 py-1 text-left text-[11px] font-semibold ${palette.chip}`}
      title={event.title}
    >
      {event.isMasked ? (
        <span className="material-symbols-outlined text-[12px] leading-none shrink-0 text-slate-500">lock</span>
      ) : isPendingLeave ? (
        <span className="material-symbols-outlined text-[12px] leading-none shrink-0">hourglass_top</span>
      ) : null}
      {event.eventType === 'meeting' && !event.isMasked ? <span className="shrink-0">{formatClock(event.startTime)} </span> : null}
      <span className="truncate">{event.title}</span>
    </button>
  )
}

function EventDetailsPopover({ event, onClose, onDelete, onEdit, deleting }) {
  if (!event) return null

  if (event.isMasked) {
    return (
      <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[4px]">
        <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-start justify-between">
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-slate-700 border border-slate-200 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-xs">lock</span>
              Busy Slot
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-on-surface-variant hover:bg-surface-container-low"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <h4 className="text-base font-bold text-on-surface">{event.title || 'Busy'}</h4>
          <p className="mt-2 text-xs leading-relaxed text-on-surface-variant">
            Event details (task name, agenda, and participants) are hidden based on the calendar owner's sharing settings.
          </p>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-surface-container-high px-4 py-2 text-xs font-semibold text-on-surface hover:bg-surface-container-highest transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isMeeting = event.eventType === 'meeting'
  const isJob = Boolean(event.jobId) || event.eventType === 'work_time' || event.scheduleType === 'JOB'
  const isLeave = event.scheduleType === 'LEAVE'
  const isHoliday = event.scheduleType === 'HOLIDAY'

  // Only calendar meetings can be edited — delete is intentionally hidden from the popover
  const canEdit = Boolean(isMeeting && event.canDelete)
  const canDelete = false // delete surfaced only via dedicated confirm flow, not modal button
  const meetingLink = String(event.meetLink || '').trim()
  const typeLabel = isLeave ? 'Leave' : isHoliday ? 'Holiday' : isMeeting ? 'Schedule Meeting' : isJob ? 'Assigned Job' : 'Event'

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[4px]">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-surface-container-high px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
              {typeLabel}
            </span>
            {event.leaveType ? (
              <span className="rounded-full bg-surface-container px-2 py-0.5 text-[11px] font-semibold text-on-surface-variant">
                {event.leaveType === 'HALF_DAY' ? 'Half Day' : 'Full Day'}
              </span>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 text-on-surface-variant hover:bg-surface-container-low">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <h4 className="text-xl font-bold text-on-surface">{event.title || '(No title)'}</h4>




        <div className="mt-4 space-y-2.5 rounded-2xl bg-surface-container-lowest p-4 border border-outline-variant/20 text-sm">
          <div className="flex items-center gap-2 text-on-surface-variant">
            <span className="material-symbols-outlined text-base">schedule</span>
            <span>
              {new Date(event.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} -{' '}
              {new Date(event.endTime).toLocaleString([], { timeStyle: 'short' })}
            </span>
          </div>

          {event.userName ? (
            <div className="flex items-center gap-2 text-on-surface">
              <span className="material-symbols-outlined text-base text-on-surface-variant">person</span>
              <span><span className="font-semibold">User:</span> {event.userName}</span>
            </div>
          ) : null}

          {event.teamName ? (
            <div className="flex items-center gap-2 text-on-surface">
              <span className="material-symbols-outlined text-base text-on-surface-variant">groups</span>
              <span><span className="font-semibold">Team:</span> {event.teamName}</span>
            </div>
          ) : null}

          {event.jobId ? (
            <div className="flex items-center gap-2 text-on-surface">
              <span className="material-symbols-outlined text-base text-on-surface-variant">assignment</span>
              <span className="font-semibold shrink-0">Job:</span>
              <DrillDownLink
                icon="assignment"
                label={event.jobName || event.jobCode || `Job #${event.jobId}`}
                to={`/nerve/job/entry?id=${event.jobId}`}
              />
            </div>
          ) : null}

          {event.taskId ? (
            <div className="flex items-center gap-2 text-on-surface">
              <span className="material-symbols-outlined text-base text-on-surface-variant">task_alt</span>
              <span className="font-semibold shrink-0">Task:</span>
              <DrillDownLink
                icon="task_alt"
                label={event.taskName || `Task #${event.taskId}`}
                to={`/nerve/task/entry?id=${event.taskId}`}
              />
            </div>
          ) : null}

          {event.projectId ? (
            <div className="flex items-center gap-2 text-on-surface">
              <span className="material-symbols-outlined text-base text-on-surface-variant">folder</span>
              <span className="font-semibold shrink-0">Project:</span>
              <DrillDownLink
                icon="folder"
                label={event.projectName || `Project #${event.projectId}`}
                to={`/nerve/project/entry?id=${event.projectId}`}
              />
            </div>
          ) : null}

          {isLeave ? (
            <div className="flex items-center gap-2 text-on-surface pt-1">
              <span className="material-symbols-outlined text-base text-on-surface-variant">event_busy</span>
              <span className="font-semibold">Leave Record:</span>
              <DrillDownLink
                icon="description"
                label="View Leave Applications"
                to="/payroll/leave-application/list"
              />
            </div>
          ) : null}

          {isLeave && event.remark ? (
            <div className="pt-2 border-t border-surface-container/60">
              <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1">Leave Remark</p>
              <p className="text-xs text-on-surface whitespace-pre-wrap">{event.remark}</p>
            </div>
          ) : null}

          {/* Sync badge */}
          {isMeeting && (event.syncToGoogle || event.syncToCalendar) ? (
            <div className="flex items-center gap-2 text-xs text-emerald-700 font-semibold">
              <span className="material-symbols-outlined text-base">sync</span>
              <span>Synced with {event.meetingProvider === 'MICROSOFT' ? 'Microsoft Outlook' : 'Google Calendar'}</span>
            </div>
          ) : null}

          {/* Meeting Link */}
          {isMeeting ? (
            <div className="pt-1">
              {meetingLink ? (
                <div className="flex items-center justify-between gap-3 rounded-xl bg-emerald-50 p-3 border border-emerald-200">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-emerald-700">videocam</span>
                    <span className="text-xs font-bold text-emerald-900">
                      {event.meetingProvider === 'MICROSOFT' ? 'Microsoft Teams' : 'Google Meet'}
                    </span>
                  </div>
                  <a
                    href={meetingLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 shadow-sm"
                  >
                    Join Now
                    <span className="material-symbols-outlined text-xs">open_in_new</span>
                  </a>
                </div>
              ) : (
                <p className="text-xs text-on-surface-variant">Meeting link not available</p>
              )}
            </div>
          ) : null}

          {/* Meeting Attendees */}
          {isMeeting && Array.isArray(event.attendees) && event.attendees.length > 0 ? (
            <div className="pt-2">
              <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1.5">
                Attendees ({event.attendees.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {event.attendees.map((attendee, index) => (
                  <span
                    key={attendee.email || index}
                    className="inline-flex items-center gap-1 rounded-full bg-surface-container px-2.5 py-0.5 text-xs font-medium text-on-surface"
                  >
                    <span className="material-symbols-outlined text-xs">mail</span>
                    {attendee.email || attendee.name || 'Participant'}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {event.description ? (
            <div className="pt-2 border-t border-surface-container/60">
              <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1">Details</p>
              <p className="text-xs text-on-surface whitespace-pre-wrap">{event.description}</p>
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/30 px-4 py-2 text-sm font-semibold text-on-surface hover:bg-surface-container-low"
          >
            Close
          </button>
          {canEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-xl border border-outline-variant/30 px-4 py-2 text-sm font-semibold text-on-surface hover:bg-surface-container-low"
            >
              Edit
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="rounded-xl bg-error px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 shadow-sm hover:opacity-90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function MonthView({ anchorDate, eventsByDate, onDayClick, onEventClick, onOverflowClick }) {
  const monthMatrix = useMemo(() => getMonthMatrix(anchorDate), [anchorDate])
  const todayKey = formatDateKey(new Date())

  return (
    <div className="overflow-hidden rounded-[22px] border border-outline-variant/20 bg-surface-container-lowest">
      <div className="grid grid-cols-7 border-b border-surface-container">
        {WEEKDAY_LABELS.map((day) => (
          <div key={day} className="px-3 py-4 text-center text-[11px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {monthMatrix.flat().map((day) => {
          const key = formatDateKey(day)
          const isCurrentMonth = day.getMonth() === anchorDate.getMonth()
          const dayEvents = eventsByDate.get(key) || []
          return (
            <div
              key={key}
              onClick={() => onDayClick(day)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onDayClick(day)
              }}
              role="button"
              tabIndex={0}
              className={`min-h-[128px] border-b border-r border-surface-container px-2 py-2 text-left align-top ${isCurrentMonth ? 'bg-white' : 'bg-surface-container-low/55'
                }`}
            >
              <span
                className={`mb-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-bold ${key === todayKey ? 'bg-primary text-white' : 'text-on-surface'
                  } ${!isCurrentMonth ? 'opacity-60' : ''}`}
              >
                {day.getDate()}
              </span>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event) => (
                  <EventPill
                    key={event.id}
                    event={event}
                    onOpen={(clickEvent) => {
                      clickEvent.stopPropagation()
                      onEventClick(event)
                    }}
                  />
                ))}
                {dayEvents.length > 3 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOverflowClick(day, dayEvents.length - 3)
                    }}
                    className="w-full text-left text-[10px] font-bold text-primary hover:underline"
                  >
                    +{dayEvents.length - 3} more
                  </button>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AgendaView({ events, onEventClick }) {
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  }, [events])

  const grouped = useMemo(() => {
    const map = new Map()
    for (const event of sortedEvents) {
      const d = new Date(event.startTime)
      const key = formatDateKey(d)
      if (!map.has(key)) {
        map.set(key, { date: d, items: [] })
      }
      map.get(key).items.push(event)
    }
    return Array.from(map.values())
  }, [sortedEvents])

  if (grouped.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[22px] border border-outline-variant/20 bg-surface-container-lowest p-12 text-center">
        <span className="material-symbols-outlined mb-2 text-5xl text-on-surface-variant/40">event_busy</span>
        <p className="text-base font-semibold text-on-surface">No upcoming events</p>
        <p className="mt-1 text-xs text-on-surface-variant">There are no meetings, leaves, or scheduled items in this window.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {grouped.map(({ date, items }) => {
        const isToday = formatDateKey(date) === formatDateKey(new Date())
        const dateTitle = date.toLocaleDateString([], {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
        return (
          <div
            key={formatDateKey(date)}
            className="overflow-hidden rounded-[22px] border border-outline-variant/20 bg-surface-container-lowest shadow-sm"
          >
            <div className={`flex items-center justify-between border-b border-surface-container px-5 py-3 ${isToday ? 'bg-primary/5' : 'bg-surface-container-low/40'}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${isToday ? 'bg-primary ring-2 ring-primary/20' : 'bg-outline-variant'}`} />
                <h3 className={`text-sm font-bold ${isToday ? 'text-primary' : 'text-on-surface'}`}>{dateTitle}</h3>
                {isToday ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary uppercase">Today</span> : null}
              </div>
              <span className="rounded-full bg-surface-container px-2.5 py-0.5 text-[11px] font-semibold text-on-surface-variant">
                {items.length} {items.length === 1 ? 'event' : 'events'}
              </span>
            </div>

            <div className="divide-y divide-surface-container/60">
              {items.map((event) => {
                const palette = resolveEventPalette(event)
                const isLeave = event.scheduleType === 'LEAVE'
                const isMeeting = event.eventType === 'meeting'
                const isPendingLeave = isLeave && event.approvalStatus === 'PENDING'
                const isRejectedLeave = isLeave && event.approvalStatus === 'REJECTED'
                return (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onEventClick(event)
                    }}
                    className="flex flex-col gap-3 p-4 transition hover:bg-surface-container-low/50 sm:flex-row sm:items-center sm:justify-between cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${palette.block}`}>
                        <span className="material-symbols-outlined text-base">
                          {isMeeting ? 'videocam' : isLeave ? 'event_busy' : event.scheduleType === 'HOLIDAY' ? 'celebration' : 'work'}
                        </span>
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className={`text-sm font-semibold text-on-surface ${isRejectedLeave ? 'line-through opacity-70' : ''}`}>
                            {event.title}
                          </h4>
                          {isPendingLeave ? (
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                              <span className="material-symbols-outlined text-[12px]">hourglass_top</span>
                              Pending Approval
                            </span>
                          ) : null}
                          {isRejectedLeave ? (
                            <span className="rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800">
                              Rejected
                            </span>
                          ) : null}
                          {isLeave && event.approvalStatus === 'APPROVED' ? (
                            <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                              Approved
                            </span>
                          ) : null}
                          {event.teamName ? (
                            <span className="rounded-md bg-surface-container-high px-2 py-0.5 text-[10px] font-medium text-on-surface-variant">
                              {event.teamName}
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-on-surface-variant">
                          {formatClock(event.startTime)} - {formatClock(event.endTime)}
                          {event.userName ? ` • ${event.userName}` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      {event.meetLink ? (
                        <a
                          href={event.meetLink}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 shadow-sm"
                        >
                          <span className="material-symbols-outlined text-sm">videocam</span>
                          Join
                        </a>
                      ) : null}
                      <span className="material-symbols-outlined text-on-surface-variant/40">chevron_right</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function WeekDayGrid({ dates, eventsByDate, onEventClick, onDayClick, onSlotClick }) {
  const dayHeight = DAY_ROW_HEIGHT * 24
  return (
    <div className="overflow-hidden rounded-[22px] border border-outline-variant/20 bg-surface-container-lowest">
      <div className={`grid border-b border-surface-container ${dates.length === 7 ? 'grid-cols-[72px_repeat(7,minmax(0,1fr))]' : 'grid-cols-[72px_minmax(0,1fr)]'}`}>
        <div className="bg-surface-container-low px-2 py-3 text-[11px] font-semibold text-on-surface-variant">GMT+05:30</div>
        {dates.map((day) => {
          const isToday = formatDateKey(day) === formatDateKey(new Date())
          return (
            <button
              key={formatDateKey(day)}
              type="button"
              onClick={() => onDayClick(day)}
              className="border-l border-surface-container px-2 py-3 text-center"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-on-surface-variant">{WEEKDAY_LABELS[day.getDay()]}</p>
              <p className={`mt-1 text-3xl font-normal ${isToday ? 'text-primary' : 'text-on-surface'}`}>{day.getDate()}</p>
            </button>
          )
        })}
      </div>

      <div className="max-h-[68vh] overflow-auto">
        <div className={`grid ${dates.length === 7 ? 'grid-cols-[72px_repeat(7,minmax(0,1fr))]' : 'grid-cols-[72px_minmax(0,1fr)]'}`}>
          <div className="relative border-r border-surface-container" style={{ height: `${dayHeight}px` }}>
            {HOUR_LABELS.map((label, hour) => (
              <div
                key={label}
                className="absolute left-0 right-0 border-t border-surface-container px-2 text-right text-[11px] text-on-surface-variant"
                style={{ top: `${hour * DAY_ROW_HEIGHT}px`, height: `${DAY_ROW_HEIGHT}px` }}
              >
                <span className="relative top-0 bg-white px-1">{label}</span>
              </div>
            ))}
          </div>

          {dates.map((day) => {
            const key = formatDateKey(day)
            const dayEvents = (eventsByDate.get(key) || []).slice().sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            return (
              <div
                key={key}
                onClick={(event) => {
                  const slot = getSlotRangeFromClick(event, dayHeight)
                  onSlotClick(day, slot.startTime, slot.endTime)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onDayClick(day)
                }}
                role="button"
                tabIndex={0}
                className="relative border-r border-surface-container text-left"
                style={{ height: `${dayHeight}px` }}
              >
                {Array.from({ length: 24 }).map((_, hour) => (
                  <div
                    key={`${key}-${hour}`}
                    className="absolute left-0 right-0 border-t border-surface-container"
                    style={{ top: `${hour * DAY_ROW_HEIGHT}px`, height: `${DAY_ROW_HEIGHT}px` }}
                  />
                ))}

                {dayEvents.map((event) => {
                  const palette = resolveEventPalette(event)
                  return (
                    <button
                      key={event.id}
                      type="button"
                      onClick={(clickEvent) => {
                        clickEvent.stopPropagation()
                        onEventClick(event)
                      }}
                      className={`absolute left-1 right-1 overflow-hidden rounded-md border-l-4 px-2 py-1 text-left text-xs shadow ${palette.block}`}
                      style={getEventBlockStyle(event, dayHeight)}
                    >
                      <p className="truncate font-semibold">{event.title || '(No title)'}</p>
                      <p className="truncate text-[10px] opacity-80">
                        {formatClock(event.startTime)} - {formatClock(event.endTime)}
                      </p>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function CalendarMainScreen() {
  const [view, setView] = useState('month')
  const [viewScope, setViewScope] = useState('personal')
  const [anchorDate, setAnchorDate] = useState(new Date())
  const [selectedMiniMonth, setSelectedMiniMonth] = useState(new Date())
  const [legendFilters, setLegendFilters] = useState({
    meetings: true,
    approvedLeave: true,
    pendingLeave: true,
    holiday: true,
    assignedJob: true,
  })
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [isActionCenterOpen, setIsActionCenterOpen] = useState(false)
  const [selectedDateForAction, setSelectedDateForAction] = useState(new Date())
  const [selectedStartTimeForAction, setSelectedStartTimeForAction] = useState('10:00')
  const [selectedEndTimeForAction, setSelectedEndTimeForAction] = useState('11:00')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [actionMode, setActionMode] = useState('create')
  const [editEvent, setEditEvent] = useState(null)
  const [deleteEvent, deleteState] = useDeleteCalendarEventMutation()

  const navigate = useNavigate()



  const activeModules = useMemo(() => parseStoredUser().modules || [], [])
  const hasNerve = activeModules.includes('nerve')

  const { data: userOptions = [] } = useGetUserOptionsQuery({ page: 1, page_size: 100 })
  const { data: departmentOptions = [] } = useGetDepartmentOptionsQuery({ page: 1, page_size: 200 })
  const { data: projectOptions = [] } = useGetProjectOptionsQuery({ page: 1, page_size: 200 }, { skip: !hasNerve })

  const queryParams = useMemo(
    () => ({
      view,
      view_scope: viewScope,
      from: formatDateKey(anchorDate),
      department_id: selectedDepartmentId || undefined,
      user_id: selectedUserId || undefined,
      project_id: selectedProjectId || undefined,
    }),
    [anchorDate, selectedDepartmentId, selectedUserId, selectedProjectId, view, viewScope],
  )

  const { data, isLoading, isError, refetch } = useGetCalendarViewQuery(queryParams)

  // OAuth callback toast & URL cleanup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('google_connected') === '1') {
      appToast.success('Google Calendar connected successfully!')
      window.history.replaceState({}, '', window.location.pathname)
      refetch()
    }
    if (params.get('microsoft_connected') === '1') {
      appToast.success('Microsoft Calendar connected successfully!')
      window.history.replaceState({}, '', window.location.pathname)
      refetch()
    }
  }, [refetch])

  const events = (data?.items || []).filter((item) => isEventVisible(item, legendFilters, hasNerve))
  const eventsByDate = useMemo(() => groupByDate(events), [events])
  const miniDays = useMemo(() => getMiniMonthMatrix(selectedMiniMonth), [selectedMiniMonth])

  function movePeriod(direction) {
    setAnchorDate((prev) => {
      const next = new Date(prev)
      if (view === 'day') {
        next.setDate(prev.getDate() + direction)
      } else if (view === 'week') {
        next.setDate(prev.getDate() + direction * 7)
      } else if (view === 'agenda') {
        next.setDate(prev.getDate() + direction * 14)
      } else {
        next.setMonth(prev.getMonth() + direction)
      }
      return next
    })
  }

  function moveMiniMonth(direction) {
    setSelectedMiniMonth((prev) => {
      const next = new Date(prev)
      next.setMonth(prev.getMonth() + direction)
      return next
    })
  }

  function openActionCenterForDate(dateValue, startTime = '10:00', endTime = '11:00') {
    setActionMode('create')
    setEditEvent(null)
    setSelectedDateForAction(dateValue || new Date())
    setSelectedStartTimeForAction(startTime)
    setSelectedEndTimeForAction(endTime)
    setIsActionCenterOpen(true)
  }

  function openActionCenterForEdit(event) {
    if (!event) return
    const start = new Date(event.startTime)
    const end = new Date(event.endTime)
    const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`
    const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`
    setActionMode('edit')
    setEditEvent(event)
    setSelectedDateForAction(start)
    setSelectedStartTimeForAction(startTime)
    setSelectedEndTimeForAction(endTime)
    setSelectedEvent(null)
    setIsActionCenterOpen(true)
  }

  async function handleDeleteEvent() {
    if (!selectedEvent?.id) return
    const ok = await appConfirm({
      title: 'Delete Calendar Event',
      message: `Are you sure you want to delete "${selectedEvent?.title || 'this event'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await deleteEvent(selectedEvent.id).unwrap()
      setSelectedEvent(null)
      appToast.success('Event deleted successfully.')
      refetch()
    } catch {
      appToast.error('Failed to delete event. Please try again.')
    }
  }

  function toggleLegendFilter(key) {
    setLegendFilters((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const weekStart = getWeekStart(anchorDate)
  const weekDates = Array.from({ length: 7 }).map((_, index) => {
    const day = new Date(weekStart)
    day.setDate(weekStart.getDate() + index)
    return day
  })

  return (
    <div className="mx-auto w-full max-w-[1680px] px-5 py-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              const today = new Date()
              setAnchorDate(today)
              setSelectedMiniMonth(today)
            }}
            className="rounded-full border border-outline px-6 py-2 text-lg font-medium hover:bg-surface-container-low transition"
          >
            Today
          </button>
          <button type="button" onClick={() => movePeriod(-1)} className="rounded-full p-2 hover:bg-surface-container-low transition">
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <button type="button" onClick={() => movePeriod(1)} className="rounded-full p-2 hover:bg-surface-container-low transition">
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
          <h1 className="text-4xl md:text-5xl font-semibold text-on-surface">{formatToolbarTitle(anchorDate, view)}</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Top-Level Scope Toggle: Personal | Team | All */}
          <div className="flex overflow-hidden rounded-full border border-outline bg-surface-container-low p-0.5">
            {[
              { id: 'personal', label: 'Personal', icon: 'person' },
              { id: 'team', label: 'Team', icon: 'groups' },
              { id: 'all', label: 'All', icon: 'public' },
            ].map((scope) => (
              <button
                key={scope.id}
                type="button"
                onClick={() => setViewScope(scope.id)}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                  viewScope === scope.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                <span className="material-symbols-outlined text-sm">{scope.icon}</span>
                {scope.label}
              </button>
            ))}
          </div>

          {/* View Toggle: Month | Week | Day | Agenda */}
          <div className="flex overflow-hidden rounded-full border border-outline">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setView(option)}
                className={`px-4 py-2 text-sm font-semibold capitalize transition ${
                  view === option ? 'bg-primary-fixed text-primary' : 'text-on-surface-variant hover:bg-surface-container-low'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive Legend Filter Bar — fixed-width labels so toggling never shifts layout */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-4 py-2.5 text-xs shadow-sm">
        <span className="shrink-0 font-bold uppercase tracking-wider text-on-surface-variant">Filter by:</span>
        {[
          { key: 'meetings',     label: 'Meetings',       dotColor: 'bg-emerald-700', activeStyle: 'border-emerald-600 bg-emerald-50 text-emerald-900' },
          { key: 'approvedLeave',label: 'Approved Leave', dotColor: 'bg-rose-600',    activeStyle: 'border-rose-500   bg-rose-50    text-rose-900'    },
          { key: 'pendingLeave', label: 'Pending Leave',  dotColor: 'bg-amber-500',   activeStyle: 'border-amber-500  bg-amber-50   text-amber-900'  },
          { key: 'holiday',      label: 'Holiday',        dotColor: 'bg-indigo-600',  activeStyle: 'border-indigo-500 bg-indigo-50  text-indigo-900' },
          ...(hasNerve ? [{ key: 'assignedJob', label: 'Assigned Job', dotColor: 'bg-sky-600', activeStyle: 'border-sky-500 bg-sky-50 text-sky-900' }] : []),
        ].map((filter) => {
          const isActive = Boolean(legendFilters[filter.key])
          return (
            <button
              key={filter.key}
              type="button"
              onClick={() => toggleLegendFilter(filter.key)}
              style={{ minWidth: '0' }}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 font-semibold transition-colors cursor-pointer select-none ${
                isActive
                  ? `${filter.activeStyle} shadow-sm`
                  : 'border-dashed border-outline-variant/40 bg-transparent text-on-surface-variant/40 opacity-60'
              }`}
              title={isActive ? `Click to hide ${filter.label}` : `Click to show ${filter.label}`}
            >
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${isActive ? filter.dotColor : 'bg-gray-400'}`} />
              <span className="whitespace-nowrap">{filter.label}</span>
              {isActive ? (
                <span className="material-symbols-outlined text-[13px] leading-none">check</span>
              ) : (
                <span className="material-symbols-outlined text-[13px] leading-none opacity-50">close</span>
              )}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-12 gap-5">
        <aside className="col-span-12 rounded-[22px] bg-surface-container-low p-4 lg:col-span-3">
          <button
            type="button"
            onClick={() => openActionCenterForDate(new Date())}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-primary to-primary-container px-4 py-3 text-base font-semibold text-white shadow hover:opacity-95 transition"
          >
            <span className="material-symbols-outlined">add</span>
            Create Action
          </button>

          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xl font-semibold">{toMonthYear(selectedMiniMonth)}</p>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => moveMiniMonth(-1)} className="rounded-md p-1 hover:bg-surface-container-low">
                  <span className="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                <button type="button" onClick={() => moveMiniMonth(1)} className="rounded-md p-1 hover:bg-surface-container-low">
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-on-surface-variant">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((item, index) => (
                <span key={`${item}-${index}`} className="py-1">{item}</span>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-7 gap-y-1 text-center text-xs">
              {miniDays.map((day) => {
                const isCurrentMonth = day.getMonth() === selectedMiniMonth.getMonth()
                const isSelected = formatDateKey(day) === formatDateKey(anchorDate)
                return (
                  <button
                    key={formatDateKey(day)}
                    type="button"
                    onClick={() => {
                      setAnchorDate(new Date(day))
                      setSelectedMiniMonth(new Date(day))
                    }}
                    className={`mx-auto h-7 w-7 rounded-full transition ${isSelected ? 'bg-primary text-white font-bold' : isCurrentMonth ? 'text-on-surface hover:bg-surface-container-high' : 'text-on-surface-variant/40'
                      }`}
                  >
                    {day.getDate()}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-on-surface-variant">
              <span className="material-symbols-outlined">group</span>
              <span className="text-sm font-medium">Filter User</span>
            </div>
            <SearchableCombobox
              value={selectedUserId}
              onChange={setSelectedUserId}
              placeholder="All users"
              searchPlaceholder="Search users..."
              emptyText="No users found."
              items={userOptions.map((user) => ({ value: String(user.id), label: user.name }))}
            />
          </div>

          <div className="mt-3 rounded-xl bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-on-surface-variant">
              <span className="material-symbols-outlined">corporate_fare</span>
              <span className="text-sm font-medium">Filter Department</span>
            </div>
            <SearchableCombobox
              value={selectedDepartmentId}
              onChange={setSelectedDepartmentId}
              placeholder="All departments"
              searchPlaceholder="Search departments..."
              emptyText="No departments found."
              items={departmentOptions.map((d) => ({ value: String(d.id), label: d.name || d.department_name || String(d.id) }))}
            />
          </div>

          {hasNerve ? (
            <div className="mt-3 rounded-xl bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center gap-2 text-on-surface-variant">
                <span className="material-symbols-outlined">deployed_code</span>
                <span className="text-sm font-medium">Filter Project</span>
              </div>
              <SearchableCombobox
                value={selectedProjectId}
                onChange={setSelectedProjectId}
                placeholder="All projects"
                searchPlaceholder="Search projects..."
                emptyText="No projects found."
                items={projectOptions.map((p) => ({ value: String(p.id), label: p.name || p.project_name || String(p.id) }))}
              />
            </div>
          ) : null}

          <div className="mt-4">
            <CalendarSharingSettings />
          </div>

        </aside>

        <section className="col-span-12 lg:col-span-9">
          {isLoading ? (
            <div className="rounded-xl bg-surface-container-low p-10 text-center text-on-surface-variant">Loading calendar...</div>
          ) : null}
          {isError ? (
            <div className="rounded-xl bg-error-container p-4 text-error">Failed to load calendar events.</div>
          ) : null}

          {!isLoading && !isError && view === 'month' ? (
            <MonthView
              anchorDate={anchorDate}
              eventsByDate={eventsByDate}
              onDayClick={(day) => openActionCenterForDate(day)}
              onEventClick={(event) => setSelectedEvent(event)}
              onOverflowClick={(day) => {
                const dateKey = formatDateKey(day)
                navigate(`/nerve/job/list?fromDate=${dateKey}&toDate=${dateKey}`)
              }}
            />
          ) : null}

          {!isLoading && !isError && view === 'week' ? (
            <WeekDayGrid
              dates={weekDates}
              eventsByDate={eventsByDate}
              onEventClick={(event) => setSelectedEvent(event)}
              onDayClick={(day) => openActionCenterForDate(day)}
              onSlotClick={(day, startTime, endTime) => openActionCenterForDate(day, startTime, endTime)}
            />
          ) : null}

          {!isLoading && !isError && view === 'day' ? (
            <WeekDayGrid
              dates={[anchorDate]}
              eventsByDate={eventsByDate}
              onEventClick={(event) => setSelectedEvent(event)}
              onDayClick={(day) => openActionCenterForDate(day)}
              onSlotClick={(day, startTime, endTime) => openActionCenterForDate(day, startTime, endTime)}
            />
          ) : null}

          {!isLoading && !isError && view === 'agenda' ? (
            <AgendaView
              events={events}
              onEventClick={(event) => setSelectedEvent(event)}
            />
          ) : null}
        </section>
      </div>

      <BridgeActionCenterModal
        isOpen={isActionCenterOpen}
        onClose={() => {
          setIsActionCenterOpen(false)
          setActionMode('create')
          setEditEvent(null)
        }}
        defaultDate={selectedDateForAction}
        defaultStartTime={selectedStartTimeForAction}
        defaultEndTime={selectedEndTimeForAction}
        defaultTeamId={''}
        mode={actionMode}
        editEvent={editEvent}
        onCreated={() => refetch()}
      />

      <EventDetailsPopover
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onEdit={() => openActionCenterForEdit(selectedEvent)}
        onDelete={handleDeleteEvent}
        deleting={deleteState.isLoading}
      />
    </div>
  )
}
